/**
 * Shared password validation rules used across login, registration, and reset-password flows.
 *
 * Policy:
 * - Minimum 12 characters
 * - At least 2 of the 3 character types: letters, digits, symbols
 *
 * This aligns with the elevo-service backend policy and accommodates
 * browser-generated passwords that may not include symbols (e.g., Chrome).
 */

export interface PasswordRule {
  test: (v: string) => boolean;
  labelKey: string;
}

/** Individual character-type checks (displayed as a visual checklist). */
export const PASSWORD_TYPE_RULES: readonly PasswordRule[] = [
  { test: (v: string) => /[a-zA-Z]/.test(v), labelKey: 'Contains a letter' },
  { test: (v: string) => /[0-9]/.test(v), labelKey: 'Contains a number' },
  { test: (v: string) => /[^a-zA-Z0-9]/.test(v), labelKey: 'Contains a symbol' },
] as const;

/** All password rules for UI display (length + type rules). */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  { test: (v: string) => v.length >= 12, labelKey: '12+ characters' },
  ...PASSWORD_TYPE_RULES,
] as const;

/**
 * Returns true when the password satisfies the strength policy:
 * - 12+ characters AND
 * - At least 2 of 3 character types (letter, digit, symbol)
 */
export function isPasswordStrong(password: string): boolean {
  if (password.length < 12) return false;
  const typesMet = PASSWORD_TYPE_RULES.filter((r) => r.test(password)).length;
  return typesMet >= 2;
}

/**
 * Returns the list of unmet rule labels for display as error hints.
 * Only reports length error when length is insufficient;
 * only reports type error when fewer than 2 types are present.
 */
export function getPasswordErrors(password: string): string[] {
  const errors: string[] = [];
  if (password.length === 0) return errors;
  if (password.length < 12) {
    errors.push('12+ characters');
  }
  const typesMet = PASSWORD_TYPE_RULES.filter((r) => r.test(password)).length;
  if (typesMet < 2) {
    errors.push('At least 2 of: letter, number, or symbol');
  }
  return errors;
}
